import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  signInWithRedirect,
  signOut,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { useAuth } from '../../hooks/useAuth';
import { touchLastLoginForCurrentUser } from '../../lib/touchLastLogin';
import { Eye, EyeOff } from 'lucide-react';
import './Auth.css';

// Generate client after Amplify is configured
const getClient = () => generateClient<Schema>();

const SIGNUP_DEFAULT_ROLE_KEY = 'signupDefaultRole';

interface AuthProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, open directly in sign-up mode (e.g. from therapist landing). */
  initialAuthState?: 'signIn' | 'signUp';
  /** Modal overlay (default) or inline card for branded org login pages. */
  variant?: 'modal' | 'embedded';
  /** Hide sign-up link on sign-in (org-specific therapist portals). */
  hideSignUp?: boolean;
}

interface AuthError {
  name?: string;
  message?: string;
}

/** Amplify v6 throws this before signIn when a Cognito session already exists; `name` can be missing in some builds. */
function isAlreadyAuthenticatedError(err: unknown): boolean {
  const e = err as { name?: string; message?: string };
  const name = String(e?.name ?? '');
  const msg = String(e?.message ?? '').toLowerCase();
  return name === 'UserAlreadyAuthenticatedException' || msg.includes('already a signed in user');
}

const Auth: React.FC<AuthProps> = ({
  isOpen,
  onClose,
  initialAuthState = 'signIn',
  variant = 'modal',
  hideSignUp = false,
}) => {
  const { register: registerUser } = useAuth();
  const [authState, setAuthState] = useState<'signIn' | 'signUp' | 'confirmSignUp' | 'forgotPassword' | 'confirmResetPassword'>(initialAuthState);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    code: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    newPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Sync form when modal opens so Sign In / Start Free each show the right screen
  useEffect(() => {
    if (isOpen) {
      setAuthState(initialAuthState);
      setError(null);
    }
  }, [isOpen, initialAuthState]);

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      await signInWithRedirect({ provider: 'Google', customState: 'prompt=select_account' });
    } catch (e) {
      const err = e as { message?: string };
      setError(err?.message || 'Google sign-in failed.');
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Only take the first 10 digits
    const tenDigits = digits.slice(0, 10);
    
    // Add US country code (+1)
    return `+1${tenDigits}`;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Strict validation for exactly 10 digits
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  const validateName = (name: string): boolean => {
    // Only allow letters, spaces, and hyphens, max 50 characters
    const nameRegex = /^[a-zA-Z\s-]{1,50}$/;
    return nameRegex.test(name);
  };

  const validateEmail = (email: string): boolean => {
    // Basic but robust email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    
    // Clear validation error when user starts typing
    setValidationErrors(prev => ({ ...prev, [id]: undefined }));
  };

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    if (value.trim()) {
      if (!validateName(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [id]: 'Only letters, spaces, and hyphens are allowed (max 50 characters)'
        }));
      }
    }
  };

  const updateLastLogin = async () => {
    try {
      await touchLastLoginForCurrentUser();
    } catch {
      // non-blocking
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const username = formData.email.toLowerCase().trim();
    const password = formData.password;

    const finishSignInDone = async () => {
      await updateLastLogin();
      Hub.dispatch('auth', { event: 'signIn', data: { user: await getCurrentUser() } });
      onClose();
    };

    const attemptPasswordSignIn = async () => {
      const { nextStep } = await signIn({
        username,
        password,
        options: {
          authFlowType: 'USER_SRP_AUTH',
        },
      });

      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        setAuthState('confirmSignUp');
        setError('Please verify your email before signing in.');
        return;
      }

      if (nextStep.signInStep === 'DONE') {
        await finishSignInDone();
      }
    };

    try {
      // Check if user is already authenticated
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // User is already authenticated, just trigger auth event and close
          console.log('User already authenticated, skipping sign-in');
          await updateLastLogin();
          Hub.dispatch('auth', { event: 'signIn', data: { user: currentUser } });
          onClose();
          return;
        }
      } catch {
        // User is not authenticated, proceed with sign-in
      }

      await attemptPasswordSignIn();
    } catch (error: unknown) {
      const authError = error as AuthError;

      if (isAlreadyAuthenticatedError(error)) {
        try {
          let session: Awaited<ReturnType<typeof fetchAuthSession>> | null = null;
          try {
            session = await fetchAuthSession();
          } catch {
            session = null;
          }
          const tokenEmail = (
            session?.tokens?.idToken?.payload?.email as string | undefined
          )
            ?.toLowerCase()
            .trim();
          const differentUser = Boolean(tokenEmail && username && tokenEmail !== username);

          if (differentUser) {
            await signOut({ global: true });
            try {
              await attemptPasswordSignIn();
            } catch (retryErr) {
              const re = retryErr as AuthError;
              if (re.name === 'NotAuthorizedException') {
                setError('Incorrect username or password.');
              } else if (re.name === 'UserNotFoundException') {
                setError('No account found with this email.');
              } else {
                setError(re.message || 'Sign-in failed. Please try again.');
              }
            }
            return;
          }

          const currentUser = await getCurrentUser();
          await updateLastLogin();
          Hub.dispatch('auth', { event: 'signIn', data: { user: currentUser } });
          onClose();
          return;
        } catch (recoveryErr) {
          // eslint-disable-next-line no-console
          console.error('Sign-in recovery (already signed in) failed:', recoveryErr);
          setError(
            'A session is already active. Open the user menu and choose Sign out, then sign in again.',
          );
          return;
        }
      }

      // Surface exact error for debugging
      // eslint-disable-next-line no-console
      console.error('Sign-in error:', authError);

      if (authError.name === 'UserNotConfirmedException') {
        setAuthState('confirmSignUp');
        setError('Please verify your email before signing in.');
      } else if (authError.name === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
      } else if (authError.name === 'UserNotFoundException') {
        setError('No account found with this email.');
      } else if (authError.name === 'InvalidParameterException') {
        setError('Please enter a valid email and password.');
      } else {
        setError(authError.message || 'Error signing in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate email format early to avoid misleading server error mapping
      const email = formData.email.toLowerCase().trim();
      if (!validateEmail(email)) {
        throw new Error('Invalid email format. Please enter a valid email address.');
      }

      // Validate names
      const errors: { firstName?: string; lastName?: string } = {};
      if (!validateName(formData.firstName)) {
        errors.firstName = 'First name can only contain letters, spaces, and hyphens (max 50 characters).';
      }
      if (!validateName(formData.lastName)) {
        errors.lastName = 'Last name can only contain letters, spaces, and hyphens (max 50 characters).';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setLoading(false);
        return;
      }

      // Only validate if the user entered a phone number
      if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
        throw new Error('Please enter a valid 10-digit phone number');
      }

      const defaultRole = typeof localStorage !== 'undefined' ? localStorage.getItem(SIGNUP_DEFAULT_ROLE_KEY) : null;
      const phone = formData.phoneNumber && validatePhoneNumber(formData.phoneNumber) ? formatPhoneNumber(formData.phoneNumber) : '';

      if (defaultRole) {
        // Use existing register flow so User record is created with role (e.g. THERAPIST from therapist landing)
        localStorage.removeItem(SIGNUP_DEFAULT_ROLE_KEY);
        await registerUser(
          email,
          formData.password,
          formData.firstName.trim(),
          formData.lastName.trim(),
          phone,
          undefined,
          undefined,
          defaultRole
        );
        setPendingUsername(email);
        setAuthState('confirmSignUp');
        return;
      }

      const userAttributes: Record<string, string> = {
        email,
        given_name: formData.firstName.trim(),
        family_name: formData.lastName.trim(),
      };
      if (formData.phoneNumber && validatePhoneNumber(formData.phoneNumber)) {
        userAttributes.phone_number = formatPhoneNumber(formData.phoneNumber);
      }

      const { nextStep } = await signUp({
        username: email,
        password: formData.password,
        options: {
          userAttributes
        }
      });

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setPendingUsername(email);
        setAuthState('confirmSignUp');
      }
    } catch (error: unknown) {
      
      const authError = error as AuthError;
      
      // Handle specific error cases
      if (authError.name === 'UsernameExistsException') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (authError.name === 'InvalidPasswordException') {
        setError('Password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters.');
      } else if (authError.name === 'InvalidParameterException') {
        // Show Cognito's exact validation message to avoid mislabeling
        setError(authError.message || 'Invalid parameter provided.');
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Error creating account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await confirmSignUp({
        // Use the actual username used during sign-up (not the email alias)
        username: (pendingUsername || formData.email.toLowerCase().trim()),
        confirmationCode: formData.code
      });

      // After successful confirmation, sign in the user
      const { nextStep } = await signIn({
        username: formData.email.toLowerCase().trim(),
        password: formData.password,
        options: {
          authFlowType: "USER_SRP_AUTH"
        }
      });

      if (nextStep.signInStep === 'DONE') {
        // Update last login time
        await updateLastLogin();
        
        // Manually trigger Hub event to ensure App.tsx gets notified
        Hub.dispatch('auth', { event: 'signIn', data: { user: await getCurrentUser() } });
        
        // Close the auth modal and let App.tsx handle navigation
        onClose();
      }
    } catch (error: unknown) {
      const authError = error as AuthError;
      
      if (authError.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Error confirming account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setLoading(true);

    try {
      await resendSignUpCode({
        username: (pendingUsername || formData.email.toLowerCase().trim())
      });
      setError('A new verification code has been sent to your email.');
    } catch (error: unknown) {
      const authError = error as AuthError;
      
      if (authError.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait a while before requesting a new code.');
      } else {
        setError('Error sending new code. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await resetPassword({
        username: formData.email.toLowerCase().trim()
      });
      setAuthState('confirmResetPassword');
    } catch (error: unknown) {
      const authError = error as AuthError;
      
      if (authError.name === 'UserNotFoundException') {
        setError('No account found with this email.');
      } else {
        setError('Error initiating password reset. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await confirmResetPassword({
        username: formData.email.toLowerCase().trim(),
        confirmationCode: formData.code,
        newPassword: formData.newPassword
      });
      setError('Password has been reset successfully. Please sign in with your new password.');
      setAuthState('signIn');
      setFormData(prev => ({
        ...prev,
        password: '',
        code: '',
        newPassword: ''
      }));
    } catch (error: unknown) {
      const authError = error as AuthError;
      if (authError.name === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (authError.name === 'InvalidPasswordException') {
        setError('Password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters.');
      } else {
        setError('Error resetting password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (authState === 'confirmSignUp') {
      setShowCloseWarning(true);
      return;
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(SIGNUP_DEFAULT_ROLE_KEY);
    }
    setError(null);
    setAuthState('signIn');
    setFormData({
      email: '',
      password: '',
      code: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      newPassword: ''
    });
    onClose();
  };

  const handleConfirmClose = () => {
    setShowCloseWarning(false);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(SIGNUP_DEFAULT_ROLE_KEY);
    }
    setAuthState('signIn');
    setFormData({
      email: '',
      password: '',
      code: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      newPassword: ''
    });
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseWarning(false);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const renderSignIn = () => (
    <form onSubmit={handleSignIn} className="auth-form">
      <h2>Welcome Back</h2>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => {
            setFormData({ ...formData, email: e.target.value });
            if (error) setError(null); // Clear error when user starts typing
          }}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="password">Password</label>
        <div className="password-field">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={formData.password}
            onChange={(e) => {
              setFormData({ ...formData, password: e.target.value });
              if (error) setError(null); // Clear error when user starts typing
            }}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <button type="submit" className="submit-button" disabled={loading}>
        {loading ? 'Signing in...' : 'Sign In'}
      </button>
      <div style={{ textAlign: 'center', margin: '12px 0' }}>or</div>
      <button
        type="button"
        className="submit-button"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        Continue with Google
      </button>
      {!hideSignUp ? (
      <p className="auth-switch">
        Don't have an account?{' '}
        <button 
          type="button" 
          onClick={() => setAuthState('signUp')}
          className="link-button"
        >
          Sign Up
        </button>
      </p>
      ) : null}
      <button
        type="button"
        onClick={() => setAuthState('forgotPassword')}
        className="link-button"
      >
        Forgot Password?
      </button>
    </form>
  );

  const renderSignUp = () => (
    <form onSubmit={handleSignUp} className="auth-form">
      <h2>Create Account</h2>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            value={formData.firstName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            maxLength={50}
            placeholder="Enter your first name"
            required
          />
          {validationErrors.firstName && (
            <div className="error-message">{validationErrors.firstName}</div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            value={formData.lastName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            maxLength={50}
            placeholder="Enter your last name"
            required
          />
          {validationErrors.lastName && (
            <div className="error-message">{validationErrors.lastName}</div>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Enter your email"
          required
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="phoneNumber">Phone Number (optional)</label>
        <input
          type="tel"
          id="phoneNumber"
          placeholder="1234567890"
          value={formData.phoneNumber}
          onChange={(e) => {
            // Only allow digits and limit to 10 characters
            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, phoneNumber: value }));
          }}
          maxLength={10}
        />
        <small className="input-help">Enter 10 digits without spaces or special characters</small>
      </div>
      
      <div className="form-group">
        <label htmlFor="password">Password</label>
        <div className="password-field">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Create a password"
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>
      
      <button type="submit" className="submit-button" disabled={loading}>
        {loading ? 'Creating Account...' : 'Join TinyWins Today'}
      </button>
      <div style={{ textAlign: 'center', margin: '12px 0' }}>or</div>
      <button
        type="button"
        className="submit-button"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        Continue with Google
      </button>
      
      <p className="auth-switch">
        Already have an account?{' '}
        <button 
          type="button" 
          onClick={() => setAuthState('signIn')}
          className="link-button"
        >
          Sign In
        </button>
      </p>
      
      {error && <div className="error-message">{error}</div>}
    </form>
  );

  const renderConfirmSignUp = () => (
    <form onSubmit={handleConfirmSignUp} className="auth-form">
      <h2>Verify Your Email</h2>
      <p className="verification-info">
        Please enter the verification code sent to {formData.email}
      </p>
      <div className="form-group">
        <label htmlFor="code">Verification Code</label>
        <input
          type="text"
          id="code"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          placeholder="Enter 6-digit code"
          required
          disabled={loading}
        />
      </div>
      {error && <div className="error-message">{error}</div>}
      <button type="submit" className="submit-button" disabled={loading}>
        {loading ? 'Verifying...' : 'Verify Email'}
      </button>
      <button
        type="button"
        onClick={handleResendCode}
        className="link-button"
        disabled={loading}
      >
        Resend verification code
      </button>
      <button
        type="button"
        onClick={() => setAuthState('signIn')}
        className="link-button"
        disabled={loading}
      >
        Back to Sign In
      </button>
    </form>
  );

  const renderForgotPassword = () => (
    <form onSubmit={handleForgotPassword} className="auth-form">
      <h2>Reset Password</h2>
      <p className="verification-info">
        Enter your email address and we'll send you a verification code to reset your password.
      </p>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>
      <button type="submit" className="submit-button" disabled={loading}>
        {loading ? 'Sending Code...' : 'Send Verification Code'}
      </button>
      <button
        type="button"
        onClick={() => setAuthState('signIn')}
        className="link-button"
      >
        Back to Sign In
      </button>
      {error && <div className="error-message">{error}</div>}
    </form>
  );

  const renderConfirmResetPassword = () => (
    <form onSubmit={handleConfirmResetPassword} className="auth-form">
      <h2>Reset Password</h2>
      <p className="verification-info">
        Please enter the verification code sent to {formData.email} and your new password.
      </p>
      <div className="form-group">
        <label htmlFor="code">Verification Code</label>
        <input
          type="text"
          id="code"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          placeholder="Enter 6-digit code"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="newPassword">New Password</label>
        <div className="password-field">
          <input
            type={showNewPassword ? 'text' : 'password'}
            id="newPassword"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowNewPassword((v) => !v)}
            aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showNewPassword}
          >
            {showNewPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>
      <button type="submit" className="submit-button" disabled={loading}>
        {loading ? 'Resetting Password...' : 'Reset Password'}
      </button>
      <button
        type="button"
        onClick={() => setAuthState('forgotPassword')}
        className="link-button"
      >
        Back
      </button>
      {error && <div className="error-message">{error}</div>}
    </form>
  );

  const formContent = showCloseWarning ? (
    <div className="warning-dialog">
      <h3>Are you sure?</h3>
      <p>If you close now, you'll need to start the verification process again.</p>
      <div className="warning-buttons">
        <button onClick={handleCancelClose} className="cancel-button">
          Stay Here
        </button>
        <button onClick={handleConfirmClose} className="confirm-button">
          Yes, Close
        </button>
      </div>
    </div>
  ) : (
    <>
      {authState === 'signIn' && renderSignIn()}
      {authState === 'signUp' && renderSignUp()}
      {authState === 'confirmSignUp' && renderConfirmSignUp()}
      {authState === 'forgotPassword' && renderForgotPassword()}
      {authState === 'confirmResetPassword' && renderConfirmResetPassword()}
    </>
  );

  if (variant === 'embedded') {
    return <div className="auth-embedded">{formContent}</div>;
  }

  return (
    <>
      <div className="auth-overlay" onClick={handleBackdropClick}>
        <div className="auth-modal">
          <button 
            className="close-button" 
            onClick={handleClose}
            aria-label="Close modal"
          >
            ×
          </button>
          {formContent}
        </div>
      </div>
    </>
  );
};

export default Auth; 